import type { TaskStatus } from "@/lib/core/crm/types";
import { formatTaskStatus } from "@/lib/schemas/task";

/**
 * Collect a unique, ordered list of assignee user IDs from a task row.
 * Supports both legacy single-assignee fields (`assigned_to`, `assigned_user`)
 * and the multi-assignee `assigned_users` array (strings or `{id}` objects).
 */
export function collectAssigneeIds(task: Record<string, unknown>): string[] {
  const ids: string[] = [];
  const raw = task.assigned_users;
  if (Array.isArray(raw)) {
    for (const u of raw) {
      if (typeof u === "string" && u.trim()) {
        ids.push(u);
      } else if (
        u &&
        typeof u === "object" &&
        "id" in u &&
        typeof (u as { id: string }).id === "string"
      ) {
        ids.push((u as { id: string }).id);
      }
    }
  }
  const assignedTo = task.assigned_to;
  if (typeof assignedTo === "string" && assignedTo.trim()) ids.push(assignedTo);
  const au = task.assigned_user as { id?: string } | undefined;
  if (au?.id) ids.push(au.id);
  return [...new Set(ids)];
}

/** Render a date string with month/day/year, returning em-dash when missing. */
export function formatTaskDate(value?: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a task status while keeping the original tab's relaxed input typing.
 * The status column in storage is a plain string; `formatTaskStatus` requires
 * the discriminated `TaskStatus` union, so we narrow at the boundary.
 */
export function formatStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return formatTaskStatus(status as TaskStatus);
}
